import { useCallback, useEffect, useState } from 'react'
import _ from 'lodash'
import queryString from 'query-string'
import {
  forwardHealthiePromise,
  providerSupportPromise,
  updateUserHealthieInfoPromise,
  uploadSelectionPromise,
  userAutoLoginPromise,
  verifyMagicLinkPromise,
} from 'apis/FitOnCareAPI'
import { encryptWithMd5, showNotification } from 'utils/basic'
import { amplitudeLogin, setAmplitudeOption } from 'utils/amplitudeInit'
import {
  brazeLogin,
  logBrazeEvent,
  setBrazeProfile,
  setBrazeUserAttribute,
} from 'utils/brazeInit'
import {
  getFitonUserId,
  getHealthieUserId,
  getToken,
  setFitonUserId,
  setHealthieProviderId,
  setHealthieToken,
  setHealthieUserId,
  setIsStateChanged,
  setStateId,
  setToken,
  setUserEmail,
  setUserId,
} from 'utils/authLocalStorage'
import { HEALTHIE_GET_USER_STRING } from 'apis/HealthieApiClient'
import {
  useClearUserInfo,
  useHealthieInfo,
  useHearAbout,
  useI18n,
  useSurvey,
  useTalkAboutCoverage,
  useUnavailableStatus,
} from 'contexts/AuthContext'
import { showErrorMessage } from './careHooks'
import {
  getInsuranceProviderForAmplitude,
  ScreenKey,
  UploadSelectionOptionType,
} from 'views/Care/util'
import { useUserProfileStatus } from './useUserProfileStatus'
import { UnavailableStatus } from 'views/Care/Unavailable'
import { useMCA } from 'views/Care/MCA/useMCA'
import {
  isMcaStateRestricted,
  setRestrictedState,
} from 'views/Care/MCA/mcaStateGate'
export const useAutoLogin = ({
  fitonUserId,
  sku,
  supportForNewWelcome,
  email,
  t,
  h,
  utmCampaign,
  utmContent,
  utmSource,
}) => {
  const [autoLoginLoading, setAutoLoginLoading] = useState(true)
  const [autoLoginSuccess, setAutoLoginSuccess] = useState(false)
  const { r, code } = queryString.parse(window.location.search)
  const { i18n } = useI18n()
  const [healthieUser, setHealthieUser] = useState({})
  const [autoLoginErrorCode, setAutoLoginErrorCode] = useState(null)
  const { clearAll } = useClearUserInfo()
  useHealthieInfo({ user: healthieUser, supportForNewWelcome })
  const { getUserProfileStatus } = useUserProfileStatus()

  const userVerifyMagicLink = useCallback(async () => {
    if (r === 'magiclink') {
      const rep = await verifyMagicLinkPromise(code)

      if (!_.isEmpty(rep) && rep.code === 200) {
        await _localLogin(rep.data)
        setAutoLoginLoading(false)
      } else {
        setAutoLoginLoading(false)
        showNotification({
          type: 'error',
          title: i18n(rep.msg || 'Verify failed'),
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r, code])

  const userAutoLogin = useCallback(async () => {
    const u = encryptWithMd5(fitonUserId)
    const e = encryptWithMd5(email)
    const user = await userAutoLoginPromise({ u, e, t, h })
    if (!_.isEmpty(user) && user.code === 200) {
      await _localLogin(user.data)
      setAutoLoginLoading(false)
    } else {
      setAutoLoginErrorCode(user?.code)
      getUserInfo({ skipClearAll: true })
    }
  }, [email, fitonUserId, t, h, sku])

  useEffect(() => {
    if (r && code) {
      userVerifyMagicLink()
    } else if (email && fitonUserId && t && h) {
      userAutoLogin()
    } else {
      getUserInfo()
    }
  }, [r, code, userVerifyMagicLink, email, fitonUserId, t, h])

  const handleUserAutoLogin = async ({ fitonUserId, email, t, h }) => {
    const u = encryptWithMd5(fitonUserId)
    const e = encryptWithMd5(email)
    const user = await userAutoLoginPromise({ u, e, t, h })
    if (!_.isEmpty(user) && user.code === 200) {
      await _localLogin(user.data)
    }
    return user
  }

  const getUserInfo = async ({ skipClearAll = false } = {}) => {
    const token = getToken()
    const currentFitonUserId = getFitonUserId()
    if (
      fitonUserId &&
      currentFitonUserId &&
      fitonUserId !== currentFitonUserId &&
      !skipClearAll
    ) {
      clearAll()
    } else if (token) {
      const [healthieUser] = await Promise.all([
        getHealthieUser(),
        getUserProfileStatus(),
      ])
      setHealthieUser(healthieUser)
    }
    if (fitonUserId) {
      setFitonUserId(fitonUserId)
    }
    setAutoLoginLoading(false)
  }

  const _localLogin = async (user) => {
    onLoginSuccess(user)
    const [healthieUser] = await Promise.all([
      getHealthieUser(),
      getUserProfileStatus(),
    ])
    if (healthieUser) {
      setBrazeProfile({
        id: user.id,
        healthieID: user.healthieUserId,
        firstName: healthieUser.first_name,
        lastName: healthieUser.last_name,
        email: healthieUser.email,
        phone: healthieUser.phone,
        utmCampaign,
        utmContent,
        utmSource,
        ...(sku !== 'mca' && { sku }),
      })
      setHealthieUser(healthieUser)
      setHealthieProviderId(healthieUser.dietitian_id)
    }
    setAutoLoginSuccess(true)
  }

  const getHealthieUser = async () => {
    const data = await forwardHealthiePromise({
      query: HEALTHIE_GET_USER_STRING,
      variables: {
        id: getHealthieUserId(),
        include_tags: true,
      },
      operationName: 'getUser',
    })
    if (data?.code && data.code !== 200) {
      showErrorMessage(data, i18n)
      if (data.code === 401) {
        clearAll()
      }
    } else if (data?.user) {
      setHealthieUser(data.user)
    }
    return data?.user || {}
  }

  const onLoginSuccess = (user) => {
    if (_.isEmpty(user)) return {}
    setUserId(user.id)
    setHealthieUserId(user.healthieUserId)
    setToken(user.token)
    setStateId(user.localeStateId)
    setHealthieToken('')
    setIsStateChanged(user.isStateChanged)
    setUserEmail(user.email)
    amplitudeLogin()
    brazeLogin()
    const updatePromises = [
      ...(user.otherHealthieUsers?.map((otherUser) =>
        updateUserHealthieInfoPromise({
          healthieUserId: otherUser.healthieUserId,
          stateType: otherUser.stateType,
          stateId: otherUser.localeStateId,
          active: false,
        })
      ) || []),
      updateUserHealthieInfoPromise({
        healthieUserId: user.healthieUserId,
        stateType: user.stateType,
        stateId: user.localeStateId,
        active: true,
      }),
    ]

    Promise.all(updatePromises)
  }

  return {
    autoLoginLoading,
    autoLoginSuccess,
    healthieUser,
    setHealthieUser,
    getHealthieUser,
    onLoginSuccess,
    setAutoLoginSuccess,
    autoLoginErrorCode,
    handleUserAutoLogin,
  }
}

export const useLoginSuccess = ({
  supportForNewWelcome,
  setCurrentKey,
  fitonEmail,
  email,
  shouldSkipScheduledTime,
  utmSource,
  rehabSelectedThemes,
  sku,
}) => {
  const { talkAboutCoverage } = useTalkAboutCoverage()
  const { getUserProfileStatus } = useUserProfileStatus()
  const { setUnavailableStatus } = useUnavailableStatus()
  const { survey } = useSurvey()
  const { hearAbout } = useHearAbout()
  const { getCurrentEnrollment } = useMCA({ sku })

  const checkProviderSupported = useCallback(
    async (sku, state) => {
      const providerSupport = await providerSupportPromise({
        stateId: state.id,
        insuranceProviderId: talkAboutCoverage.id,
      })
      let isProviderSupport = false
      switch (sku) {
        case 'pt':
          isProviderSupport = providerSupport.ptAccept
          break
        case 'wlc':
          isProviderSupport = providerSupport.wlcAccept
          break
        default:
          isProviderSupport = providerSupport.accept
          break
      }
      return { providerSupport: isProviderSupport }
    },
    [talkAboutCoverage]
  )

  const updateInsuranceProvider = useCallback(async () => {
    uploadSelectionPromise({
      optionType: UploadSelectionOptionType.INSURANCE_PROVIDER_OPTION,
      selectedOptionId: talkAboutCoverage.id,
      otherInputText: talkAboutCoverage.otherText,
    })
    const provider = getInsuranceProviderForAmplitude(talkAboutCoverage)
    setBrazeUserAttribute('Insurance Provider', provider)
    setAmplitudeOption('Insurance - Provider', provider)
  }, [talkAboutCoverage])

  const getHealthieUser = async () => {
    const data = await forwardHealthiePromise({
      query: HEALTHIE_GET_USER_STRING,
      variables: {
        id: getHealthieUserId(),
        include_tags: true,
      },
      operationName: 'getUser',
    })
    return data?.user || {}
  }

  const openAutoLoginScreen = useCallback(
    async (state, sku, theme, shouldSkipScheduledTime) => {
      if (fitonEmail) {
        setAmplitudeOption('FitOn Email', fitonEmail)
      }
      if (email) {
        setAmplitudeOption('User Email', email)
      }

      if (sku === 'mca' && isMcaStateRestricted(state)) {
        setRestrictedState(state)
        return ScreenKey.MCA_COMING_SOON_STATE
      }

      if (!state.available) {
        setUnavailableStatus(UnavailableStatus.AUTO_LOGIN_STATE_UNAVAILABLE)
        return ScreenKey.UNAVAILABLE
      }

      const [healthieProfile, userProfile] = await Promise.all([
        getHealthieUser(),
        getUserProfileStatus({
          forceFetch: true,
        }),
      ])
      const status = userProfile.skuStatus?.[sku]

      switch (sku) {
        case 'msk':
          if (!theme) {
            return ScreenKey.PAIN_LOCATED
          } else {
            return ScreenKey.REHAB_QUESTION_1
          }
        case 'wlc':
          if (status === 2) {
            if (shouldSkipScheduledTime) {
              return ScreenKey.SKU_CONGRATULATIONS
            } else {
              return ScreenKey.SCHEDULED_TIME
            }
          } else {
            if (healthieProfile.gender) {
              const wlcPrimary = userProfile.skuCondition?.wlc_primary
              if (
                wlcPrimary &&
                status !== 5 &&
                !wlcPrimary.includes('26') &&
                !wlcPrimary.includes('27')
              ) {
                if (!_.isEmpty(hearAbout) || utmSource) {
                  if (userProfile.insuranceProvided) {
                    return shouldSkipScheduledTime
                      ? ScreenKey.SKU_CONGRATULATIONS
                      : ScreenKey.SCHEDULED_TIME
                  } else {
                    return ScreenKey.TALK_ABOUT_COVERAGE
                  }
                } else {
                  return ScreenKey.HEAR_ABOUT
                }
              } else {
                return ScreenKey.PAST_DIAGNOSIS
              }
            } else {
              return ScreenKey.COMPLETE_PROFILE
            }
          }
        case 'pt':
          if (status === 2) {
            return ScreenKey.SCHEDULED_TIME
          } else {
            if (healthieProfile.gender) {
              if (userProfile.skuCondition?.pt_primary && status !== 5) {
                if (!_.isEmpty(hearAbout) || utmSource) {
                  if (userProfile.insuranceProvided) {
                    return ScreenKey.SCHEDULED_TIME
                  } else {
                    return ScreenKey.TALK_ABOUT_COVERAGE
                  }
                } else {
                  return ScreenKey.HEAR_ABOUT
                }
              } else {
                return ScreenKey.PAIN_LOCATED
              }
            } else {
              return ScreenKey.COMPLETE_PROFILE
            }
          }
        case 'mca':
          const { currentKey } = await getCurrentEnrollment()
          return currentKey
        default:
          if (status === 2) {
            return ScreenKey.SCHEDULED_TIME
          } else if (userProfile.skuCondition?.rd_primary) {
            if (
              userProfile.skuCondition?.rd_primary?.includes('3') &&
              _.isEmpty(userProfile.skuCondition?.rd_secondary)
            ) {
              return ScreenKey.HORMONAL_AUTOIMMUNE
            } else if (healthieProfile.gender) {
              if (!_.isEmpty(hearAbout) || utmSource) {
                if (userProfile.insuranceProvided) {
                  return ScreenKey.SCHEDULED_TIME
                } else {
                  return ScreenKey.TALK_ABOUT_COVERAGE
                }
              } else {
                return ScreenKey.HEAR_ABOUT
              }
            } else {
              return ScreenKey.COMPLETE_PROFILE
            }
          } else {
            return ScreenKey.SURVEY
          }
      }
    },
    [fitonEmail, email, hearAbout, utmSource]
  )

  const openSkipScreen = useCallback(
    async (state, sku) => {
      if (fitonEmail) {
        setAmplitudeOption('FitOn Email', fitonEmail)
      }
      if (email) {
        setAmplitudeOption('User Email', email)
      }

      const { shouldSkipFlow } = await getUserProfileStatus({
        forceFetch: true,
      })

      if (!state.available) {
        const status =
          sku === 'rd' || sku === 'pt'
            ? UnavailableStatus.COMING_SOON_STATE
            : UnavailableStatus.NOT_ELIGIBLE_STATE

        setUnavailableStatus(status)
        setCurrentKey(ScreenKey.UNAVAILABLE)
        return
      }

      switch (sku) {
        case 'msk':
          setCurrentKey(
            shouldSkipFlow
              ? shouldSkipScheduledTime
                ? ScreenKey.REHAB_CONGRATULATIONS
                : ScreenKey.SCHEDULED_TIME
              : ScreenKey.COMPLETE_PROFILE
          )
          break
        case 'wlc':
          setCurrentKey(
            shouldSkipFlow
              ? ScreenKey.PAST_DIAGNOSIS
              : ScreenKey.COMPLETE_PROFILE
          )
          break
        case 'pt':
          if (!_.isEmpty(supportForNewWelcome)) {
            const { providerSupport } = await checkProviderSupported(sku, state)
            if (!providerSupport) {
              const status = UnavailableStatus.NOT_ELIGIBLE_STATE
              setUnavailableStatus(status)
              setCurrentKey(ScreenKey.UNAVAILABLE)
            } else {
              setCurrentKey(
                shouldSkipFlow
                  ? ScreenKey.PAIN_LOCATED
                  : ScreenKey.COMPLETE_PROFILE
              )
            }
            updateInsuranceProvider()
          } else {
            setCurrentKey(
              shouldSkipFlow
                ? ScreenKey.PAIN_LOCATED
                : ScreenKey.COMPLETE_PROFILE
            )
          }
          break
        default:
          if (!_.isEmpty(supportForNewWelcome)) {
            const { providerSupport } = await checkProviderSupported(sku)
            if (!providerSupport) {
              setCurrentKey(ScreenKey.INSURANCE_NOT_ACCEPTED)
            } else {
              logBrazeEvent('User Approved')
              const isSurveyFilled = !_.isEmpty(survey)
              setCurrentKey(
                shouldSkipFlow && isSurveyFilled
                  ? ScreenKey.SCHEDULED_TIME
                  : ScreenKey.SURVEY
              )
            }
            updateInsuranceProvider()
          } else {
            if (supportForNewWelcome?.providerSupport) {
              logBrazeEvent('User Approved')
            }
            const isSurveyFilled = !_.isEmpty(survey)
            if (shouldSkipFlow && isSurveyFilled) {
              setCurrentKey(ScreenKey.SCHEDULED_TIME)
            } else {
              setCurrentKey(ScreenKey.SURVEY)
            }
          }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      checkProviderSupported,
      getUserProfileStatus,
      setCurrentKey,
      supportForNewWelcome,
      fitonEmail,
      email,
      shouldSkipScheduledTime,
      survey,
    ]
  )

  return { openSkipScreen, openAutoLoginScreen }
}
